/**
 * Service for dynamic management of third-party scripts and cookies.
 */

const GA_ID = import.meta.env.VITE_GA_ID || 'G-XXXXXXXXXX'
const GTM_ID = import.meta.env.VITE_GTM_ID || 'GTM-XXXXXXX'
const PIXEL_ID = import.meta.env.VITE_PIXEL_ID || 'XXXXXXXXXXXXXXX'
const TIKTOK_ID = import.meta.env.VITE_TIKTOK_PIXEL_ID
const GOOGLE_ADS_ID = import.meta.env.VITE_GOOGLE_ADS_CONVERSION_ID
const GOOGLE_ADS_LABEL = import.meta.env.VITE_GOOGLE_ADS_CONVERSION_LABEL

export const cookieService = {
  /**
   * Updates Google Consent Mode based on user preferences.
   * @param {Object} preferences - User cookie preferences.
   */
  loadScripts(preferences) {
    const consentUpdate = {
      'analytics_storage': preferences.analytics ? 'granted' : 'denied',
      'ad_storage': preferences.marketing ? 'granted' : 'denied',
      'ad_user_data': preferences.marketing ? 'granted' : 'denied',
      'ad_personalization': preferences.marketing ? 'granted' : 'denied'
    }

    // Send update to Google Consent Mode
    if (window.gtag) {
      window.gtag('consent', 'update', consentUpdate)
    }

    if (preferences.marketing) {
      this.injectMetaPixel(PIXEL_ID)
      if (TIKTOK_ID) {
        this.injectTikTokPixel(TIKTOK_ID)
      }
    }
  },

  /**
   * Meta Pixel script injection
   */
  injectMetaPixel(id) {
    if (document.getElementById('meta-pixel')) return

    const script = document.createElement('script')
    script.id = 'meta-pixel'
    script.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${id}');
      fbq('track', 'PageView');
    `
    document.head.appendChild(script)
  },

  /**
   * TikTok Pixel script injection
   */
  injectTikTokPixel(id) {
    if (document.getElementById('tiktok-pixel')) return

    const script = document.createElement('script')
    script.id = 'tiktok-pixel'
    script.innerHTML = `
      !function (w, d, t) {
        w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
        ttq.load('${id}');
        ttq.page();
      }(window, document, 'ttq');
    `
    document.head.appendChild(script)
  },

  /**
   * Removes third-party script tags and clears associated cookies.
   */
  clearScripts() {
    // Reset Google Consent Mode to denied
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied',
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied'
      })
    }

    // Remove remaining scripts (Meta Pixel & TikTok Pixel)
    const ids = ['meta-pixel', 'tiktok-pixel']
    ids.forEach(id => {
      const el = document.getElementById(id)
      if (el) el.remove()
    })

    // Clear cookies
    this.deleteCookie('_ga')
    this.deleteCookie('_gid')
    this.deleteCookie('_fbp')
    this.deleteCookie('_ttp')
    
    // Clear all _ga_* cookies
    const cookies = document.cookie.split(';')
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim()
      if (cookie.startsWith('_ga_')) {
        const name = cookie.split('=')[0]
        this.deleteCookie(name)
      }
    }
  },

  /**
   * Helper to set a technical cookie for audit compliance.
   */
  setConsentCookie(id) {
    const date = new Date()
    date.setTime(date.getTime() + (365 * 24 * 60 * 60 * 1000)) // 1 year
    document.cookie = `bb_consent_id=${id}; expires=${date.toUTCString()}; Path=/; SameSite=Lax`
  },

  /**
   * Helper to delete a cookie.
   */
  deleteCookie(name) {
    document.cookie = name + '=; Path=/; Domain=' + window.location.hostname + '; Expires=Thu, 01 Jan 1970 00:00:01 GMT;'
    document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;'
  },

  /**
   * Tracks a standard Meta Pixel event if consent is granted.
   * @param {string} eventName - The name of the event (e.g., 'Purchase', 'AddToCart')
   * @param {Object} params - Event parameters
   */
  trackPixelEvent(eventName, params = {}) {
    if (window.fbq && typeof window.fbq === 'function') {
      // Ensure numeric values for optimization
      if (params.value !== undefined) {
        params.value = Number(params.value)
      }
      window.fbq('track', eventName, params)
    }
  },

  /**
   * Tracks a standard TikTok Pixel event if consent is granted.
   * @param {string} eventName - The name of the event
   * @param {Object} params - Event parameters
   */
  trackTikTokEvent(eventName, params = {}) {
    if (window.ttq && typeof window.ttq.track === 'function') {
      // Ensure numeric values for optimization
      if (params.value !== undefined) {
        params.value = Number(params.value)
      }
      window.ttq.track(eventName, params)
    }
  },

  /**
   * Tracks a GA4 Ecommerce event via dataLayer.
   * @param {string} eventName - GA4 event name (view_item, add_to_cart, etc.)
   * @param {Object} ecommerce - Ecommerce data object
   */
  trackGA4EcommerceEvent(eventName, ecommerce = {}) {
    try {
      if (window.dataLayer) {
        // Best practice: reset ecommerce object before pushing new one
        window.dataLayer.push({ ecommerce: null })
        window.dataLayer.push({
          event: eventName,
          ecommerce: ecommerce
        })
      }
    } catch (error) {
      // Silent catch as per requirements
    }
  },

  /**
   * Tracks a Google Ads Conversion.
   * REMOVED: Redundant with GTM implementation.
   * Keeping function signature for compatibility but disabling direct call.
   * @param {string} transactionId - Unique transaction identifier
   * @param {number|string} value - Conversion value
   */
  trackGoogleAdsConversion(transactionId, value) {
    // Disabled to avoid duplication with GTM (GTM-K9MFQ73J)
    /*
    try {
      if (window.gtag && GOOGLE_ADS_ID && GOOGLE_ADS_LABEL) {
        window.gtag('event', 'conversion', {
          'send_to': `${GOOGLE_ADS_ID}/${GOOGLE_ADS_LABEL}`,
          'value': Number(value),
          'currency': 'EUR',
          'transaction_id': transactionId
        })
      }
    } catch (error) {
      // Silent catch
    }
    */
  }
}
